import BaseApiService from '../BaseApiService.js'
import apiClient from '../api.js'
import { env } from '@/utils/env.js'

/**
 * Department Management API Service
 * Handles all department-related API operations
 */
class DepartmentService extends BaseApiService {
  constructor() {
    super('departments') // Laravel API endpoint for departments
  }

  /**
   * Get all departments with pagination and filters
   * @param {object} params - Query parameters
   * @param {string} params.search - Search query
   * @param {string} params.status - Status filter
   * @param {string} params.parent_id - Parent department filter
   * @param {number} params.per_page - Items per page
   * @param {number} params.page - Current page
   * @returns {Promise} API response
   */
  async getDepartments(params = {}) {
    try {
      const response = await this.get('', params)
      env.log('Departments retrieved successfully')
      return response
    } catch (error) {
      console.error('DepartmentService.getDepartments error:', error)
      env.error('Failed to retrieve departments:', error)
      throw error
    }
  }

  /**
   * Get departments for dropdown (simplified format)
   * @returns {Promise} API response with departments list
   */
  async getDepartmentsDropdown() {
    try {
      const response = await apiClient.get('departments-dropdown')
      env.log('Departments dropdown retrieved successfully')
      return response.data
    } catch (error) {
      console.error('DepartmentService.getDepartmentsDropdown error:', error)
      env.error('Failed to retrieve departments dropdown:', error)
      throw error
    }
  }

  /**
   * Create a new department
   * @param {object} departmentData - Department data
   * @param {string} departmentData.name - Department name
   * @param {string} departmentData.department_code - Department code
   * @param {number} departmentData.parent_department_id - Parent department ID
   * @param {string} departmentData.description - Department description
   * @param {string} departmentData.status - Department status
   * @returns {Promise} API response
   */
  async createDepartment(departmentData) {
    try {
      const response = await this.post(departmentData)
      env.log('Department created successfully')
      return response
    } catch (error) {
      env.error('Failed to create department:', error)
      throw error
    }
  }

  /**
   * Get a specific department by ID
   * @param {string|number} id - Department ID
   * @returns {Promise} API response
   */
  async getDepartment(id) {
    try {
      const response = await this.get(id)
      env.log('Department retrieved successfully')
      return response
    } catch (error) {
      env.error('Failed to retrieve department:', error)
      throw error
    }
  }

  /**
   * Update an existing department
   * @param {string|number} id - Department ID
   * @param {object} departmentData - Updated department data
   * @returns {Promise} API response
   */
  async updateDepartment(id, departmentData) {
    try {
      const response = await this.put(id, departmentData)
      env.log('Department updated successfully')
      return response
    } catch (error) {
      env.error('Failed to update department:', error)
      throw error
    }
  }

  /**
   * Delete a department
   * @param {string|number} id - Department ID
   * @returns {Promise} API response
   */
  async deleteDepartment(id) {
    try {
      const response = await this.delete(id)
      env.log('Department deleted successfully')
      return response
    } catch (error) {
      env.error('Failed to delete department:', error)
      throw error
    }
  }

  /**
   * Get department statistics
   * @returns {Promise} API response with statistics
   */
  async getDepartmentStatistics() {
    try {
      const response = await this.getDepartments({ per_page: 1 })
      return {
        success: true,
        data: response.data?.statistics || {
          total_departments: 0,
          active_departments: 0,
          inactive_departments: 0,
          root_departments: 0
        }
      }
    } catch (error) {
      env.error('Failed to retrieve department statistics:', error)
      throw error
    }
  }

  /**
   * Validate department data
   * @param {object} departmentData - Department data to validate
   * @returns {object} Validation result
   */
  validateDepartmentData(departmentData) {
    const errors = {}

    // Required fields
    if (!departmentData.name?.trim()) {
      errors.name = 'Department name is required'
    }

    if (!departmentData.department_code?.trim()) {
      errors.department_code = 'Department code is required'
    }

    // Validate department code format (English letters only)
    if (departmentData.department_code && !/^[A-Za-z]+$/.test(departmentData.department_code)) {
      errors.department_code = 'Department code must contain English letters only (A-Z)'
    }

    // Validate status
    if (departmentData.status && !['active', 'inactive'].includes(departmentData.status)) {
      errors.status = 'Status must be either active or inactive'
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
}

// Export a singleton instance
export default new DepartmentService()